"use client";

import {
    useState,
    useEffect,
    useMemo,
    useRef,
    useCallback,
    useTransition,
} from "react";
import { useSearchParams } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { toast } from "sonner";
import { useRouter } from "@/i18n/navigation";
import { useRegistrationStore } from "@/state/stores/registrationStore";
import { isBackendConfigured } from "@/api/api-client";
import { pullBookingParticipantsIntoStore } from "@/lib/bookingParticipantsRemoteSync";
import { deleteAztangofestBookingParticipant } from "@/api/aztangofest/deleteAztangofestBookingParticipant";
import { executeRecaptchaAction } from "@/controllers/RecaptchaController";
import {
    decodeJwtSub,
    getAzDanceBookingToken,
    isAzDanceBookingTokenValid,
} from "@/lib/azdanceBookingSession";
import { formatApiError } from "@/lib/apiErrorMessage";
import { bumpRegistrationMaxStepClient } from "@/lib/registrationFlowCookie";
import { fetchAztangofestEvent } from "@/api/aztangofest/fetchAztangofestEvent";
import { getDefaultEventSlug } from "@/lib/eventSlug";
import {
    submitProfileRegistrationToBackend,
    registeredParticipantToProfilePayload,
} from "@/api/aztangofest/submitProfileRegistration";
import type { RegisteredParticipant } from "@/state/stores/registrationStore";
import {
    registrationFlowParticipantFieldErrors,
    isRegistrationFlowParticipantCompletelyValid,
} from "@/lib/validation/registrationFlowParticipant";
import { useFormValidationMessages } from "@/hooks/useFormValidationMessages";
import type { ParticipantFormData } from "./registrationFlowTypes";
import {
    buildRegisteredParticipantBlocks,
    computeInitialPrimaryFormIndex,
    mapRegisteredParticipantsToParticipantFormData,
    mergeCurrentUserIntoRegisteredParticipants,
    primaryFormRowIndexForCurrentUser,
} from "./registrationFlowMappers";
import {
    emptyParticipantFormData,
    mergeParticipantFormRowUpdate,
} from "./registrationFlowParticipantFormMerge";
import { registrationFlowSyncBookingParticipantsToApi } from "./registrationFlowSyncBookingParticipants";
import { dedupeRegisteredParticipantsById } from "@/lib/registeredParticipantDedupe";
import {
    optionalPhoneError,
    requiredPhoneError,
    toCallingCountryCode,
} from "@/lib/intlPhoneValidation";
import { isRegistrationFlowPrimaryParticipantRow } from "./registrationFlowPrimaryParticipant";
import { trackCtaClick, trackFunnelStep, trackFrontendError } from "@/lib/telemetry/yandex";

/** Непустой ввод в форме: любая непустая строка в данных участника (без перечисления полей). */
function registrationFlowRowsHaveAnyStringInput(
    rows: ParticipantFormData[],
): boolean {
    for (const p of rows) {
        for (const v of Object.values(p)) {
            if (typeof v === "string" && v.trim().length > 0) {
                return true;
            }
        }
    }
    return false;
}

function buildInitialParticipantsFromStore(): ParticipantFormData[] | null {
    const { registeredParticipants, currentUser } =
        useRegistrationStore.getState();
    const merged = mergeCurrentUserIntoRegisteredParticipants(
        registeredParticipants,
        currentUser,
    );
    if (merged.length === 0) return null;
    return mapRegisteredParticipantsToParticipantFormData(merged);
}

function initialParticipantsSnapshot(): ParticipantFormData[] {
    const fromStore = buildInitialParticipantsFromStore();
    if (fromStore) return fromStore;

    return [emptyParticipantFormData()];
}

function initialPrimaryFormIndexSnapshot(
    forms: ParticipantFormData[],
): number {
    const { currentUser } = useRegistrationStore.getState();
    if (currentUser && forms.length > 0) {
        return primaryFormRowIndexForCurrentUser(forms, currentUser);
    }
    if (forms.length > 0 && isAzDanceBookingTokenValid()) {
        const token = getAzDanceBookingToken();
        const sub = token ? decodeJwtSub(token) : null;
        if (sub) {
            const idx = forms.findIndex((row) => row.id === sub);
            if (idx >= 0) return idx;
        }
    }
    return computeInitialPrimaryFormIndex();
}

const PARTICIPANT_ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/i;

/** После bootstrap `POST …/booking/participants` заменить локальный id владельца сессии на ULID с сервера. */
function remapRegisteredParticipantPrimaryId(
    list: RegisteredParticipant[],
    oldId: string,
    newId: string,
): RegisteredParticipant[] {
    if (oldId === newId) return list;
    return list.map((p) => ({
        ...p,
        id: p.id === oldId ? newId : p.id,
        partnerId:
            p.partnerId && p.partnerId === oldId ? newId : p.partnerId,
    }));
}

export function useRegistrationFlowPage() {
    const router = useRouter();
    const searchParams = useSearchParams();
    const locale = useLocale();
    const language =
        typeof locale === "string" && locale.length > 0 ? locale : "en";
    const t = useTranslations("strings.registrationFlow");
    const formValidationMessages = useFormValidationMessages();
    const {
        registeredParticipants,
        registerParticipants,
        returnAfterRegistration,
        setReturnUrl,
        setCabinetOwnerParticipantId,
        currentUser,
        dashboardEventBookingId,
        setDashboardEventBookingId,
    } = useRegistrationStore();

    /**
     * На сервере store без localStorage; на клиенте до первого paint persist может
     * уже отдать данные из storage — тогда `initialParticipantsSnapshot()` не совпадёт
     * с SSR и React даст hydration mismatch (иконка/бордер карточки).
     * Первый ренер на клиенте сознательно совпадает с SSR; фактическое состояние
     * приходит в `useEffect` (syncParticipantsFromStore / onFinishHydration).
     */
    const [participants, setParticipants] =
        useState<ParticipantFormData[]>(() =>
            typeof window === "undefined"
                ? initialParticipantsSnapshot()
                : [emptyParticipantFormData()],
        );

    const [primaryFormIndex, setPrimaryFormIndex] = useState<number>(() => {
        const forms =
            typeof window === "undefined"
                ? initialParticipantsSnapshot()
                : [emptyParticipantFormData()];
        return initialPrimaryFormIndexSnapshot(forms);
    });

    const [errors, setErrors] = useState<
        Record<number, Record<string, string>>
    >({});
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [guestAutoSaveLastAt, setGuestAutoSaveLastAt] =
        useState<Date | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isNavPending, startRegistrationNav] = useTransition();
    /** API + переход на следующий шаг; при возврате со страницы (в т.ч. bfcache) сбрасывается. */
    const isSubmitting = isSaving || isNavPending;

    /**
     * До первого прохода sync + `setPostMount(true)` совпадаем с SSR (нет JWT из `document`,
     * не показываем данные из persist). Флаг включается в конце того же эффекта, что подтягивает формы.
     */
    const [postMount, setPostMount] = useState(false);

    const participantsRef = useRef(participants);
    participantsRef.current = participants;

    const refreshParticipantsList = useCallback(
        async (opts?: { isCancelled?: () => boolean }) => {
            const isCancelled = opts?.isCancelled;
            const merged = await pullBookingParticipantsIntoStore(language, {
                isCancelled,
            });
            if (isCancelled?.() || !merged?.length) return;
            const forms =
                mapRegisteredParticipantsToParticipantFormData(merged);
            setParticipants(forms);
            const user = useRegistrationStore.getState().currentUser;
            if (user) {
                setPrimaryFormIndex(
                    primaryFormRowIndexForCurrentUser(forms, user),
                );
            } else if (isAzDanceBookingTokenValid()) {
                const t = getAzDanceBookingToken();
                const sub = t ? decodeJwtSub(t) : null;
                const idx =
                    sub != null
                        ? forms.findIndex((row) => row.id === sub)
                        : -1;
                setPrimaryFormIndex(
                    idx >= 0 ? idx : computeInitialPrimaryFormIndex(),
                );
            } else {
                setPrimaryFormIndex(computeInitialPrimaryFormIndex());
            }
            setErrors({});
        },
        [language],
    );

    useEffect(() => {
        trackFunnelStep("registration_flow", "view");
    }, []);

    /** Гость: черновик анкет в Zustand → persist/localStorage (стабильные id в форме). */
    useEffect(() => {
        if (currentUser) return;
        if (isAzDanceBookingTokenValid()) return;
        const timer = window.setTimeout(() => {
            const rows = participantsRef.current;
            if (!registrationFlowRowsHaveAnyStringInput(rows)) return;
            const blocks = buildRegisteredParticipantBlocks(rows);
            const flat = blocks.flat();
            if (flat.length === 0) return;
            setParticipants((prev) => {
                let changed = false;
                const next = prev.map((row, i) => {
                    const primary = blocks[i]?.[0];
                    if (!primary || row.id) return row;
                    changed = true;
                    return { ...row, id: primary.id };
                });
                return changed ? next : prev;
            });
            registerParticipants(flat);
            setGuestAutoSaveLastAt(new Date());
        }, 900);
        return () => window.clearTimeout(timer);
    }, [participants, currentUser, registerParticipants]);

    /** После rehydrate из localStorage формы должны подтянуть сохранённых участников. */
    useEffect(() => {
        const syncParticipantsFromStore = () => {
            const { registeredParticipants: reg, currentUser: user } =
                useRegistrationStore.getState();
            const merged = mergeCurrentUserIntoRegisteredParticipants(reg, user);
            if (merged.length === 0) return;
            const forms =
                mapRegisteredParticipantsToParticipantFormData(merged);
            setParticipants(forms);
            if (user) {
                setPrimaryFormIndex(
                    primaryFormRowIndexForCurrentUser(forms, user),
                );
            } else if (isAzDanceBookingTokenValid()) {
                const t = getAzDanceBookingToken();
                const sub = t ? decodeJwtSub(t) : null;
                const idx =
                    sub != null
                        ? forms.findIndex((row) => row.id === sub)
                        : -1;
                setPrimaryFormIndex(
                    idx >= 0 ? idx : computeInitialPrimaryFormIndex(),
                );
            } else {
                setPrimaryFormIndex(computeInitialPrimaryFormIndex());
                setGuestAutoSaveLastAt(new Date());
            }
            setErrors({});
        };

        syncParticipantsFromStore();
        const unsub = useRegistrationStore.persist.onFinishHydration(() => {
            syncParticipantsFromStore();
            setPostMount(true);
        });
        setPostMount(true);
        return unsub;
    }, []);

    useEffect(() => {
        const q = searchParams.get("eventBookingId")?.trim();
        if (q) setDashboardEventBookingId(q);
    }, [searchParams, setDashboardEventBookingId]);

    /** После успешного submit не снимаем «сохранение» до ухода со страницы; bfcache иначе оставил бы спиннер. */
    useEffect(() => {
        const onPageShow = (e: PageTransitionEvent) => {
            if (e.persisted) setIsSaving(false);
        };
        window.addEventListener("pageshow", onPageShow);
        return () => window.removeEventListener("pageshow", onPageShow);
    }, []);

    useEffect(() => {
        if (participants.length === 1) {
            setPrimaryFormIndex(0);
        }
    }, [participants.length]);

    useEffect(() => {
        if (participants.length === 0) return;
        setPrimaryFormIndex((i) => Math.min(i, participants.length - 1));
    }, [participants.length]);

    const cabinetOwnerParticipantId = useRegistrationStore(
        (s) => s.cabinetOwnerParticipantId,
    );

    const registrationCabinetUserId = useMemo(() => {
        if (!postMount) return null;
        return (
            currentUser?.id ??
            (isAzDanceBookingTokenValid()
                ? decodeJwtSub(getAzDanceBookingToken() || "") || null
                : null)
        );
    }, [postMount, currentUser?.id]);

    const primaryParticipantOpts = useMemo(
        () => ({
            sessionParticipantId: registrationCabinetUserId,
            cabinetOwnerParticipantId,
        }),
        [registrationCabinetUserId, cabinetOwnerParticipantId],
    );

    const isPrimaryParticipantRow = useCallback(
        (index: number) =>
            isRegistrationFlowPrimaryParticipantRow(
                participants[index],
                index,
                participants,
                primaryFormIndex,
                primaryParticipantOpts,
            ),
        [participants, primaryFormIndex, primaryParticipantOpts],
    );

    const updateParticipant = (
        index: number,
        data: Partial<ParticipantFormData>,
    ) => {
        const prev = participants[index];
        const nextRow = mergeParticipantFormRowUpdate(prev, data);
        const updated = [...participants];
        updated[index] = nextRow;

        if (prev.isChampionshipParticipant && !nextRow.isChampionshipParticipant) {
            registerParticipants(
                buildRegisteredParticipantBlocks(updated).flat(),
            );
        }

        setParticipants(updated);
        setErrors((prevErr) => {
            const currentRowErrors = { ...(prevErr[index] ?? {}) };

            const phoneErr = isPrimaryParticipantRow(index)
                ? requiredPhoneError(
                      nextRow.phone,
                      {
                          required: formValidationMessages.phoneRequired,
                          invalid: formValidationMessages.phoneInvalid,
                      },
                      toCallingCountryCode(nextRow.country),
                  )
                : optionalPhoneError(
                      nextRow.phone,
                      {
                          invalid: formValidationMessages.phoneInvalid,
                      },
                      toCallingCountryCode(nextRow.country),
                  );
            if (phoneErr) currentRowErrors.phone = phoneErr;
            else delete currentRowErrors.phone;

            if (nextRow.isChampionshipParticipant && !nextRow.isSolo) {
                const partner2PhoneErr = optionalPhoneError(
                    nextRow.partner2Phone ?? "",
                    { invalid: formValidationMessages.phoneInvalid },
                    toCallingCountryCode(nextRow.partner2Country),
                );
                if (partner2PhoneErr)
                    currentRowErrors.partner2Phone = partner2PhoneErr;
                else delete currentRowErrors.partner2Phone;
            } else {
                for (const k of Object.keys(currentRowErrors)) {
                    if (k.startsWith("partner2")) delete currentRowErrors[k];
                }
            }

            if (Object.keys(currentRowErrors).length === 0) {
                if (!prevErr[index]) return prevErr;
                const nextErrors = { ...prevErr };
                delete nextErrors[index];
                return nextErrors;
            }
            return { ...prevErr, [index]: currentRowErrors };
        });
    };

    const toggleParticipantExpansion = (index: number) => {
        const updated = [...participants];
        updated[index].isExpanded = !updated[index].isExpanded;
        setParticipants(updated);
    };

    const validateParticipant = (index: number) => {
        const p = participants[index];
        const newErrors = registrationFlowParticipantFieldErrors(p, formValidationMessages, {
            mainParticipantPhoneOptional: !isPrimaryParticipantRow(index),
        });

        if (Object.keys(newErrors).length > 0) {
            setErrors({ ...errors, [index]: newErrors });
            return false;
        }
        const updatedErrors = { ...errors };
        delete updatedErrors[index];
        setErrors(updatedErrors);
        return true;
    };

    const isParticipantValid = (index: number) => {
        return isRegistrationFlowParticipantCompletelyValid(
            participants[index],
            formValidationMessages,
            {
                mainParticipantPhoneOptional: !isPrimaryParticipantRow(index),
            },
        );
    };

    const handleAddParticipant = () => {
        trackCtaClick("registration_add_participant", {
            placement: "registration_flow",
            participants_count: participants.length,
        });
        const updated = participants.map((p) => ({ ...p, isExpanded: false }));
        setParticipants([...updated, emptyParticipantFormData()]);

        setTimeout(() => {
            window.scrollTo({
                top: document.body.scrollHeight,
                behavior: "smooth",
            });
        }, 100);
    };

    const participantRowCanRemove = (index: number) => {
        if (participants.length <= 1) return false;
        const id = participants[index]?.id?.trim();
        const token = getAzDanceBookingToken();
        const sub = token ? decodeJwtSub(token) : null;
        if (sub && id === sub) return false;
        return true;
    };

    const handleRemoveParticipant = async (index: number) => {
        if (participants.length === 1) return;
        if (!participantRowCanRemove(index)) return;

        const row = participants[index];
        const token = getAzDanceBookingToken();
        const eid =
            useRegistrationStore.getState().dashboardEventBookingId?.trim();
        const participantId = row.id?.trim() ?? "";
        const jwtSub = token ? decodeJwtSub(token) : null;

        const updated = participants.filter((_, i) => i !== index);
        const nextRegisteredFlat =
            buildRegisteredParticipantBlocks(updated).flat();

        const updatedErrors = { ...errors };
        delete updatedErrors[index];

        const reindexedErrors: Record<number, Record<string, string>> = {};
        Object.keys(updatedErrors).forEach((key) => {
            const oldIndex = parseInt(key, 10);
            const newIndex = oldIndex > index ? oldIndex - 1 : oldIndex;
            reindexedErrors[newIndex] = updatedErrors[oldIndex]!;
        });

        const snapshot = {
            participants,
            errors,
            primaryFormIndex,
            registeredParticipants: [
                ...useRegistrationStore.getState().registeredParticipants,
            ],
        };

        const applyLocalRemove = () => {
            setParticipants(updated);
            setPrimaryFormIndex((prev) => {
                if (updated.length <= 1) return 0;
                if (index < prev) return prev - 1;
                if (index === prev) return Math.min(prev, updated.length - 1);
                return prev;
            });
            setErrors(reindexedErrors);
            registerParticipants(nextRegisteredFlat);
        };

        const rollback = () => {
            setParticipants(snapshot.participants);
            setPrimaryFormIndex(snapshot.primaryFormIndex);
            setErrors(snapshot.errors);
            registerParticipants(snapshot.registeredParticipants);
        };

        const needsServerDelete =
            isBackendConfigured() &&
            Boolean(eid) &&
            Boolean(token) &&
            PARTICIPANT_ULID_RE.test(participantId) &&
            participantId !== jwtSub;

        if (needsServerDelete) {
            applyLocalRemove();
            try {
                await deleteAztangofestBookingParticipant({
                    participantId,
                    language,
                    accessToken: token!,
                });
                await refreshParticipantsList();
            } catch (err) {
                rollback();
                toast.error(formatApiError(err));
            }
        } else {
            applyLocalRemove();
        }
    };

    const handleSubmit = async () => {
        let allValid = true;
        participants.forEach((_, index) => {
            if (!validateParticipant(index)) {
                allValid = false;
            }
        });

        if (!allValid) {
            trackFunnelStep("registration_flow", "error", {
                reason: "validation_failed",
                participants_count: participants.length,
            });
            alert(t("alertValidationFailed"));
            const firstInvalidIndex = participants.findIndex(
                (_, i) => !isParticipantValid(i),
            );
            if (firstInvalidIndex >= 0) {
                const updated = [...participants];
                updated[firstInvalidIndex].isExpanded = true;
                setParticipants(updated);
            }
            return;
        }

        trackFunnelStep("registration_flow", "submit", {
            participants_count: participants.length,
            has_return_url: Boolean(returnAfterRegistration),
        });

        const blocks = buildRegisteredParticipantBlocks(participants);
        let validParticipants = dedupeRegisteredParticipantsById(
            blocks.flat(),
        );
        const safePrimaryIdx = Math.min(
            Math.max(0, primaryFormIndex),
            Math.max(0, blocks.length - 1),
        );
        const primaryBlock = blocks[safePrimaryIdx] ?? blocks[0];
        let profileOwnerParticipant = primaryBlock?.[0];

        const loggedInUser = useRegistrationStore.getState().currentUser;
        const bookingToken = getAzDanceBookingToken();
        if (loggedInUser) {
            const match = validParticipants.find(
                (p) => p.id === loggedInUser.id,
            );
            if (match) profileOwnerParticipant = match;
        } else if (
            bookingToken &&
            isAzDanceBookingTokenValid()
        ) {
            const sub = decodeJwtSub(bookingToken);
            if (sub) {
                const match = validParticipants.find((p) => p.id === sub);
                if (match) profileOwnerParticipant = match;
            }
        }

        if (!profileOwnerParticipant) {
            alert(t("alertSaveFailed"));
            return;
        }

        setIsSaving(true);
        let willNavigateAway = false;
        try {
        /**
         * Гость без сессии брони: как на `/profile-registration` — bootstrap
         * `POST /azdance/booking/participants` (event_id + primary), затем upsert участников с JWT.
         */
        if (
            isBackendConfigured() &&
            !loggedInUser &&
            !isAzDanceBookingTokenValid()
        ) {
            try {
                const slug = getDefaultEventSlug();
                const event = await fetchAztangofestEvent(slug, language);
                if (event?.id) {
                    useRegistrationStore.getState().setAzdanceEventId(event.id);
                }
                if (!event?.id) {
                    trackFunnelStep("registration_flow", "error", {
                        reason: "event_not_found",
                    });
                    toast.error(t("alertSaveFailed"));
                    return;
                }
                const r = await submitProfileRegistrationToBackend(
                    {
                        event_id: event.id,
                        participant: registeredParticipantToProfilePayload(
                            profileOwnerParticipant,
                        ),
                    },
                    language,
                );
                if (!r.ok) {
                    trackFunnelStep("registration_flow", "error", {
                        reason: "profile_bootstrap_rejected",
                    });
                    toast.error(r.error || t("alertSaveFailed"));
                    return;
                }
                if (
                    "serverProfileId" in r &&
                    r.serverProfileId &&
                    r.serverProfileId !== profileOwnerParticipant.id
                ) {
                    validParticipants = remapRegisteredParticipantPrimaryId(
                        validParticipants,
                        profileOwnerParticipant.id,
                        r.serverProfileId,
                    );
                    profileOwnerParticipant =
                        validParticipants.find(
                            (p) => p.id === r.serverProfileId,
                        ) ?? {
                            ...profileOwnerParticipant,
                            id: r.serverProfileId,
                        };
                }
            } catch (err) {
                trackFunnelStep("registration_flow", "error", {
                    reason: "profile_bootstrap_exception",
                });
                trackFrontendError("registration_flow_bootstrap", err);
                toast.error(t("alertSaveFailed"));
                return;
            }
        }

        setCabinetOwnerParticipantId(profileOwnerParticipant.id);
        registerParticipants(validParticipants);

        try {
            await registrationFlowSyncBookingParticipantsToApi(language, {
                draftUpsertParticipants: validParticipants,
                reloadAfterSync: async (_lang) => {
                    await refreshParticipantsList();
                },
            });
        } catch (err) {
            trackFunnelStep("registration_flow", "error", {
                reason: "sync_failed",
            });
            trackFrontendError("registration_flow_sync", err);
            toast.error(formatApiError(err));
            return;
        }

        if (returnAfterRegistration) {
            const returnUrl = returnAfterRegistration;
            setReturnUrl(null);
            if (returnUrl.includes("/cart")) bumpRegistrationMaxStepClient(4);
            else if (returnUrl.includes("/activities"))
                bumpRegistrationMaxStepClient(3);
            willNavigateAway = true;
            startRegistrationNav(() => {
                router.push(returnUrl);
            });
        } else {
            willNavigateAway = true;
            startRegistrationNav(() => {
                router.push("/registration-cards");
            });
        }
        } finally {
            if (!willNavigateAway) setIsSaving(false);
        }
    };

    const allParticipantsValid = participants.every((_, index) =>
        isParticipantValid(index),
    );

    const showGuestAutosaveBanner = useMemo(() => {
        if (!postMount) return false;
        if (currentUser) return false;
        if (isAzDanceBookingTokenValid()) return false;
        if (registeredParticipants.length >= 1) return true;
        return registrationFlowRowsHaveAnyStringInput(participants);
    }, [postMount, currentUser, registeredParticipants.length, participants]);

    const registeredParticipantsForUi = postMount ? registeredParticipants : [];

    return {
        registeredParticipants: registeredParticipantsForUi,
        participants,
        primaryFormIndex,
        errors,
        showInfoModal,
        setShowInfoModal,
        updateParticipant,
        toggleParticipantExpansion,
        isParticipantValid,
        handleAddParticipant,
        handleRemoveParticipant,
        participantRowCanRemove,
        handleSubmit,
        isSubmitting,
        allParticipantsValid,
        setPrimaryFormIndex,
        registrationCabinetUserId,
        cabinetOwnerParticipantId,
        showGuestAutosaveBanner,
        guestAutoSaveLastAt,
    };
}
